import { useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@marine/ui/badge';
import { Button } from '@marine/ui/button';
import { Input } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import { Separator } from '@marine/ui/separator';
import {
  useTenantAiKeyOverrides,
  useUpdateTenantAiKeyOverrides,
  type UpdateTenantAiKeysInput,
} from '@/modules/platform/services/aiSettingsApi';

interface TenantAiKeyOverridesSectionProps {
  tenantId: string;
}

type ProviderField =
  | 'anthropic_api_key_override'
  | 'openrouter_api_key_override'
  | 'perplexity_api_key_override'
  | 'openai_api_key_override';

const PROVIDERS: Array<{
  field: ProviderField;
  label: string;
  placeholder: string;
  configKey: 'anthropic' | 'openrouter' | 'perplexity' | 'openai';
}> = [
  { field: 'anthropic_api_key_override',  label: 'Anthropic',  placeholder: 'sk-ant-…', configKey: 'anthropic'  },
  { field: 'openrouter_api_key_override', label: 'OpenRouter', placeholder: 'sk-or-…',  configKey: 'openrouter' },
  { field: 'perplexity_api_key_override', label: 'Perplexity', placeholder: 'pplx-…',   configKey: 'perplexity' },
  { field: 'openai_api_key_override',     label: 'OpenAI',     placeholder: 'sk-…',     configKey: 'openai'     },
];

export function TenantAiKeyOverridesSection({ tenantId }: TenantAiKeyOverridesSectionProps) {
  const { data, isLoading } = useTenantAiKeyOverrides(tenantId);
  const updateMutation = useUpdateTenantAiKeyOverrides(tenantId);
  const [drafts, setDrafts] = useState<Partial<Record<ProviderField, string>>>({});

  const handleSave = (field: ProviderField, value: string) => {
    const payload: UpdateTenantAiKeysInput = { [field]: value || null };
    updateMutation.mutate(payload, {
      onSuccess: () => {
        toast.success(`${labelFor(field)} override saved`);
        setDrafts((prev) => ({ ...prev, [field]: '' }));
      },
      onError: () => {
        toast.error(`Failed to save ${labelFor(field)} override`);
      },
    });
  };

  const handleClear = (field: ProviderField) => {
    if (! confirm(`Clear the ${labelFor(field)} override? Tenant will fall back to the platform default.`)) {
      return;
    }
    updateMutation.mutate({ [field]: null } as UpdateTenantAiKeysInput, {
      onSuccess: () => {
        toast.success(`${labelFor(field)} override cleared`);
        setDrafts((prev) => ({ ...prev, [field]: '' }));
      },
      onError: () => {
        toast.error(`Failed to clear ${labelFor(field)} override`);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading AI key overrides…</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="ms-1 flex items-center gap-1.5 text-sm font-semibold">
          <Sparkles className="size-3.5 opacity-60" />
          AI Key Overrides
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          When set, these per-tenant keys take precedence over the platform defaults
          at <code>Platform → AI Settings</code>. Leave empty to use the platform default.
        </p>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {PROVIDERS.map((p) => {
          const masked = data?.masked_keys[p.configKey];
          const configured = data?.configured[p.configKey] ?? false;
          const draft = drafts[p.field] ?? '';

          return (
            <div key={p.field} className="space-y-2 rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">{p.label}</p>
                {configured ? (
                  <Badge variant="success" appearance="light" size="sm">
                    Override active
                  </Badge>
                ) : (
                  <Badge variant="secondary" size="sm">
                    Using platform default
                  </Badge>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor={p.field} className="text-xs text-muted-foreground">
                  {configured ? `Current: ${masked}` : 'No tenant override set'}
                </Label>
                <Input
                  id={p.field}
                  type="password"
                  placeholder={p.placeholder}
                  value={draft}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [p.field]: e.target.value }))}
                  disabled={updateMutation.isPending}
                />
              </div>

              <div className="flex justify-end gap-2">
                {configured && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleClear(p.field)}
                    disabled={updateMutation.isPending}
                  >
                    Clear
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  onClick={() => handleSave(p.field, draft)}
                  disabled={updateMutation.isPending || draft === ''}
                >
                  {updateMutation.isPending ? (
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                  ) : null}
                  Save
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Separator />

      <p className="text-xs text-muted-foreground">
        Override precedence: tenant override → platform default → 503 error to dispatcher.
        Use overrides for "BYOK" tenants or to isolate noisy tenants on a separate quota.
      </p>
    </div>
  );
}

function labelFor(field: ProviderField): string {
  return PROVIDERS.find((p) => p.field === field)?.label ?? field;
}
